import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Copy, Mail, AlertCircle, CheckCircle, Trash2, Search, Building2, Send } from 'lucide-react';
import { clubInvitationService, clubService, emailService } from '../../services/supabaseService';
import '../admin/JudgeInvitationManager.css';
import logger from '../../utils/logger';

const ClubInvitationManager = ({ events = [], auth = null }) => {
  const [step, setStep] = useState('list'); // 'list' | 'generate'
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);

  // Clubs registrados
  const [allClubs, setAllClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(false);

  // Formulario
  const [formData, setFormData] = useState({
    eventId: '',
    selectedClubId: '',
    clubEmail: '',
    clubName: '',
    manualEntry: false,
    federatedFilter: 'all' // 'all' | 'federated' | 'non-federated'
  });

  // Cargar clubes registrados
  useEffect(() => {
    const loadClubs = async () => {
      setClubsLoading(true);
      try {
        const { data } = await clubService.getAll();
        setAllClubs(data || []);
      } catch (err) {
        logger.error('Error loading clubs:', err?.message);
      } finally {
        setClubsLoading(false);
      }
    };
    loadClubs();
  }, []);

  // Clubes activos para el combo con filtro de federados
  const activeClubs = useMemo(() => {
    let clubs = allClubs.filter(c => c.is_active !== false);
    if (formData.federatedFilter === 'federated') {
      clubs = clubs.filter(c => c.is_federated);
    } else if (formData.federatedFilter === 'non-federated') {
      clubs = clubs.filter(c => !c.is_federated);
    }
    return clubs;
  }, [allClubs, formData.federatedFilter]);

  // Al seleccionar un club del combo
  const handleClubSelect = (clubId) => {
    if (clubId === 'manual') {
      setFormData({
        ...formData,
        selectedClubId: '',
        clubEmail: '',
        clubName: '',
        manualEntry: true
      });
      return;
    }

    const club = activeClubs.find(c => c.id === clubId);
    if (club) {
      setFormData({
        ...formData,
        selectedClubId: club.id,
        clubEmail: club.email || '',
        clubName: club.name || '',
        manualEntry: false
      });
    } else {
      setFormData({
        ...formData,
        selectedClubId: '',
        clubEmail: '',
        clubName: '',
        manualEntry: false
      });
    }
  };

  // Generar nueva invitación
  const handleGenerateInvitation = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!formData.eventId) {
      setError('Por favor selecciona un evento');
      setLoading(false);
      return;
    }

    if (!formData.clubName.trim()) {
      setError('Por favor selecciona un club o ingresa el nombre');
      setLoading(false);
      return;
    }

    if (!formData.clubEmail.trim()) {
      setError('Por favor ingresa el correo del club');
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.clubEmail)) {
      setError('Por favor ingresa un correo válido');
      setLoading(false);
      return;
    }

    try {
      const adminId = auth?.id || auth?.user?.id;

      if (!adminId) {
        setError('Error: Usuario no autenticado');
        setLoading(false);
        return;
      }

      const result = await clubInvitationService.generateInvitation(
        adminId,
        formData.eventId,
        formData.selectedClubId || null,
        formData.clubEmail,
        formData.clubName
      );

      if (result.error) {
        setError(typeof result.error === 'string' ? result.error : 'Error al generar invitación');
        setLoading(false);
        return;
      }

      const event = events.find(e => e.id === formData.eventId);
      const eventName = event ? event.name : 'Evento';

      const newInvitation = {
        ...result.data,
        club_email: formData.clubEmail,
        club_name: formData.clubName,
        event_name: eventName,
        email_sent: false
      };

      // Intentar enviar correo automáticamente
      const emailResult = await emailService.sendClubInvitationCode(
        formData.clubEmail,
        formData.clubName,
        result.data.code,
        eventName
      );

      if (emailResult.error) {
        logger.error('⚠️ Error enviando correo:', emailResult.error);
        newInvitation.email_sent = false;
        setSuccess(`✅ Código generado: ${result.data.code}\n⚠️ No se pudo enviar el correo automáticamente. Puedes copiarlo y enviarlo manualmente.`);
      } else {
        newInvitation.email_sent = true;
        setSuccess(`✅ Código generado y correo enviado a ${formData.clubEmail}`);
      }

      setInvitations([newInvitation, ...invitations]);

      // Reset form
      setFormData({
        eventId: '',
        selectedClubId: '',
        clubEmail: '',
        clubName: '',
        manualEntry: false
      });

      setTimeout(() => {
        setStep('list');
        setSuccess('');
      }, 3000);
    } catch (err) {
      logger.error('Error:', err?.message);
      setError('Error al generar invitación');
    } finally {
      setLoading(false);
    }
  };

  // Copiar código
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Reenviar correo
  const handleResendEmail = async (invitation) => {
    setSendingEmail(invitation.id);
    setError('');

    try {
      const result = await emailService.sendClubInvitationCode(
        invitation.club_email,
        invitation.club_name,
        invitation.code,
        invitation.event_name
      );

      if (result.error) {
        setError(`❌ Error al reenviar: ${typeof result.error === 'string' ? result.error : 'Intenta más tarde'}`);
      } else {
        setInvitations(prev => prev.map(inv =>
          inv.id === invitation.id ? { ...inv, email_sent: true } : inv
        ));
        setSuccess(`✅ Correo reenviado a ${invitation.club_email}`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      logger.error('Error reenviando correo:', err?.message);
      setError('Error al reenviar correo');
    } finally {
      setSendingEmail(null);
    }
  };

  // Eliminar invitación
  const handleDeleteInvitation = (invitationId) => {
    setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
  };

  return (
    <div className="invitation-manager">
      {/* Header */}
      <div className="invitation-header">
        <h3>
          {step === 'list' ? `Invitaciones enviadas (${invitations.length})` : 'Nueva Invitación de Club'}
        </h3>
        {step === 'list' ? (
          <button
            onClick={() => { setStep('generate'); setError(''); setSuccess(''); }}
            className="btn btn-primary"
          >
            <Plus size={16} />
            Nueva Invitación
          </button>
        ) : (
          <button
            onClick={() => { setStep('list'); setError(''); setSuccess(''); }}
            className="btn btn-secondary"
          >
            ← Volver a lista
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="alert success">
          <CheckCircle size={18} />
          <span style={{ whiteSpace: 'pre-line' }}>{success}</span>
        </div>
      )}

      {/* GENERATE FORM */}
      {step === 'generate' && (
        <form onSubmit={handleGenerateInvitation} className="invitation-form">
          <div className="form-info">
            <p>
              Selecciona un evento y un club registrado. Se generará un código único que el club podrá usar para darse de alta al evento desde su Portal de Club.
            </p>
          </div>

          <div className="form-group">
            <label>Evento *</label>
            <select
              value={formData.eventId}
              onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
              disabled={loading}
            >
              <option value="">-- Seleccionar evento --</option>
              {events.filter(e => e.is_active).map(event => (
                <option key={event.id} value={event.id}>
                  {event.name} — {event.date}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Filtrar Clubes</label>
            <select
              value={formData.federatedFilter}
              onChange={(e) => setFormData({ ...formData, federatedFilter: e.target.value })}
              style={{ marginBottom: 8 }}
            >
              <option value="all">Todos los clubes</option>
              <option value="federated">Solo federados</option>
              <option value="non-federated">Solo no federados</option>
            </select>
          </div>

          <div className="form-group">
            <label>Club *</label>
            <select
              value={formData.selectedClubId || (formData.manualEntry ? 'manual' : '')}
              onChange={(e) => handleClubSelect(e.target.value)}
              disabled={loading || clubsLoading}
            >
              <option value="">-- Seleccionar club registrado --</option>
              {activeClubs.map(club => (
                <option key={club.id} value={club.id}>
                  {club.name} {club.email ? `(${club.email})` : ''}
                </option>
              ))}
              <option value="manual">✏️ Ingresar datos manualmente</option>
            </select>
            {clubsLoading && (
              <span className="form-hint">Cargando clubes...</span>
            )}
          </div>

          {formData.manualEntry && (
            <div className="form-row">
              <div className="form-group">
                <label>Nombre del club *</label>
                <input
                  type="text"
                  value={formData.clubName}
                  onChange={(e) => setFormData({ ...formData, clubName: e.target.value })}
                  placeholder="Nombre del club"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Correo del club *</label>
                <input
                  type="email"
                  value={formData.clubEmail}
                  onChange={(e) => setFormData({ ...formData, clubEmail: e.target.value })}
                  placeholder="correo@club.com"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {!formData.manualEntry && formData.selectedClubId && (
            <div className="form-row">
              <div className="form-group">
                <label>Nombre</label>
                <input
                  type="text"
                  value={formData.clubName}
                  className="auto-filled"
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Correo *</label>
                <input
                  type="email"
                  value={formData.clubEmail}
                  onChange={(e) => setFormData({ ...formData, clubEmail: e.target.value })}
                  placeholder="correo@club.com"
                  disabled={loading}
                  className={formData.clubEmail && !formData.manualEntry ? 'auto-filled' : ''}
                />
                {!formData.clubEmail && (
                  <span className="form-hint warning">Este club no tiene correo registrado. Ingresa uno.</span>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary full-width"
            disabled={loading || !formData.eventId || !formData.clubName.trim() || !formData.clubEmail.trim()}
          >
            {loading ? (
              <>
                <span className="spinner-small" />
                Generando...
              </>
            ) : (
              <>
                <Send size={16} />
                Generar y Enviar Invitación
              </>
            )}
          </button>
        </form>
      )}

      {/* INVITATIONS LIST */}
      {step === 'list' && (
        <>
          {invitations.length === 0 ? (
          <div className="empty-state">
            <Mail size={48} color="#d1d5db" />
            <p>No hay invitaciones generadas aún</p>
            <button
              onClick={() => setStep('generate')}
              className="btn btn-primary"
            >
              Crear primera invitación
            </button>
          </div>
        ) : (
            <div className="invitations-list">
              {invitations.map(invitation => (
                <div key={invitation.id} className="invitation-card">
                  <div className="invitation-content">
                    <div className="invitation-info">
                      <h4>
                        <Building2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                        {invitation.club_name || 'Club'}
                      </h4>
                      <p className="email">{invitation.club_email || ''}</p>
                      <p className="event">{invitation.event_name || 'Evento'}</p>
                    </div>

                    <div className="invitation-code">
                      <code>{invitation.code}</code>
                      <button
                        onClick={() => handleCopyCode(invitation.code)}
                        className="copy-btn"
                        title="Copiar código"
                      >
                        {copiedCode === invitation.code ? (
                          <CheckCircle size={16} color="#10b981" />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </div>

                    <div className="invitation-status">
                      <span className={`status ${invitation.used ? 'used' : 'pending'}`}>
                        {invitation.used ? '✅ Canjeado' : '⏳ Pendiente'}
                      </span>
                      {invitation.email_sent && (
                        <span className="status email-sent">📧 Email enviado</span>
                      )}
                    </div>
                  </div>

                  <div className="invitation-actions">
                    {!invitation.used && (
                      <>
                        <button
                          onClick={() => handleResendEmail(invitation)}
                          disabled={sendingEmail === invitation.id}
                          className="btn-action send"
                          title="Reenviar email"
                        >
                          {sendingEmail === invitation.id ? (
                            <span className="spinner-small" />
                          ) : (
                            <Mail size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteInvitation(invitation.id)}
                          className="btn-action delete"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClubInvitationManager;
